<h2 class="page-subtitle">Roles &amp; Permissions</h2>

<p class="intro">
  The Roles &amp; Permissions page defines the responsibilities associated
  with each role. It includes narrative descriptions of role types and their
  assigned capabilities. This information helps ensure consistent and
  appropriate access across all levels of the system.
</p>

<section class="accordion">
  <button
    class="accordion-title"
    type="button"
    [attr.aria-expanded]="open.typesOfRoles"
    (click)="toggle('typesOfRoles')"
  >
    <span>Types of Roles</span>
    <fa-icon [icon]="open.typesOfRoles ? faChevronUp : faChevronDown"></fa-icon>
  </button>
  <hr class="accordion-divider" />
  <div class="accordion-body" *ngIf="open.typesOfRoles">
    <p>
      CIAO uses a set of user roles that determine what a person can see or do
      inside the application. This page explains all available roles and what
      permissions each role has across Trip Management, Team Management, and
      User Management.
    </p>
    <p>Roles fall into three categories:</p>

    <h3>Admin Roles</h3>
    <p>
      These users manage teams, assign roles, and maintain structure.
    </p>
    <ul>
      <li>System Admin</li>
      <li>Region and Station Admin</li>
      <li>Forest and Lab Admin</li>
    </ul>
    <p>
      Admins can only grant privileges at their own level or to roles beneath
      their level. All admins are responsible for managing roles correctly.
    </p>

    <h3>Team Roles</h3>
    <p>
      User is assigned to at least one team. These users participate in field
      work and team activities.
    </p>
    <ul>
      <li>Supervisor</li>
      <li>Dispatcher (coming soon)</li>
      <li>Team Member</li>
      <li>Guest</li>
    </ul>

    <h3>Registered vs. Non-Registered</h3>
    <p>These users have limited or no access.</p>
    <ul>
      <li>
        Registered User (user created a profile in CIAO but not assigned to a
        team)
      </li>
      <li>Non-Registered User (authenticated with no account created in CIAO)</li>
    </ul>
  </div>
</section>

<section class="accordion">
  <button
    class="accordion-title"
    type="button"
    [attr.aria-expanded]="open.permissionsByRole"
    (click)="toggle('permissionsByRole')"
  >
    <span>Permissions by Role</span>
    <fa-icon
      [icon]="open.permissionsByRole ? faChevronUp : faChevronDown"
    ></fa-icon>
  </button>
  <hr class="accordion-divider" />
  <div class="accordion-body" *ngIf="open.permissionsByRole">
    <p>
      CIAO uses a set of user roles that determine what a person can see or do
      inside the application. This section explains all available roles and
      what permissions each role has across Trip Management, Team Management,
      and User Management.
    </p>

    <h3>System Admin</h3>
    <ul>
      <li>Highest level of access in CIAO</li>
      <li>Can elevate users into any admin role</li>
      <li>Can manage all teams across the entire system</li>
      <li>Reserved for the CIAO development team</li>
    </ul>

    <h3>Region and Station Admin</h3>
    <ul>
      <li>
        Manages admin access for a region and its associated forests and labs
      </li>
      <li>Can create and manage teams across the entire region</li>
      <li>Can assign Region and Station Admin roles within their scope</li>
      <li>Can assign Forest and Lab Admin roles</li>
      <li>Oversees region&#8209;wide team structure and user access</li>
    </ul>

    <h3>Forest and Lab Admin</h3>
    <ul>
      <li>Manages admin access within a specific forest or lab</li>
      <li>Can create and manage teams for that specific unit</li>
      <li>Can assign Forest and Lab Admin roles within their scope</li>
      <li>
        Can assign Supervisor or Team Member roles within their forest/lab
      </li>
      <li>Oversees team structure and user access at the forest/lab level</li>
    </ul>

    <h3>Registered User</h3>
    <ul>
      <li>A new user who has logged into CIAO for the first time</li>
      <li>Has not yet been assigned an admin role or team role</li>
      <li>Has limited viewing permissions only</li>
      <li>
        Must be assigned to a team or role by an admin to gain functionality
      </li>
    </ul>
  </div>
</section>

<section class="accordion">
  <button
    class="accordion-title"
    type="button"
    [attr.aria-expanded]="open.permissionsByTeamRole"
    (click)="toggle('permissionsByTeamRole')"
  >
    <span>Permissions by Team Role</span>
    <fa-icon
      [icon]="open.permissionsByTeamRole ? faChevronUp : faChevronDown"
    ></fa-icon>
  </button>
  <hr class="accordion-divider" />
  <div class="accordion-body" *ngIf="open.permissionsByTeamRole">
    <p>
      CIAO uses a set of team roles that determine what a person can see or do
      inside a team and trip.
    </p>

    <h3>Supervisor (USDA.gov only)</h3>
    <ul>
      <li>Can add/remove all team roles</li>
      <li>Can update team information</li>
      <li>Can be a Supervisor/Admin for Crew Members on a trip</li>
      <li>Can be a Crew Member on a trip, and&hellip;</li>
      <li>Can be a POC or an Additional POC on a trip, but&hellip;</li>
      <li>Cannot hold both roles on the same trip</li>
      <li>Can view/create/update/delete all team trips</li>
    </ul>

    <h3>Team Member (USDA.gov only)</h3>
    <ul>
      <li>Can add/remove other Team Members and Guests on a team</li>
      <li>Can view/create/update/delete all trips on a team</li>
      <li>Can be a Crew Member on a trip, and&hellip;</li>
      <li>Can be an Additional POC on a trip, but&hellip;</li>
      <li>Cannot hold both roles on the same trip</li>
    </ul>

    <h3>Dispatcher (non&#8209;USDA.gov only)</h3>
    <ul>
      <li>Can add/remove another non&#8209;USDA.gov Dispatcher on a team</li>
      <li>Cannot add/remove other roles on a team</li>
      <li>Cannot be a Crew Member, POC, or Additional POC on a trip</li>
      <li>Can view/create/update all trips assigned to their team</li>
    </ul>

    <h3>Guest (non&#8209;USDA.gov only)</h3>
    <ul>
      <li>Can add/remove other Guests on a team</li>
      <li>Cannot be a Supervisor/Admin on a team</li>
      <li>Cannot be a Team Member on a team</li>
      <li>
        Can be elevated to a Dispatcher on a team by a Supervisor/Admin or
        another Dispatcher
      </li>
      <li>Can only view/create/update trips where they are a Crew Member</li>
      <li>
        Cannot view/create/update trips for other Team Members and Guests
      </li>
      <li>Cannot be a POC or Additional POC on a trip</li>
    </ul>
  </div>
</section>

<hr class="section-divider" />

<div class="kba-section">
  <p>
    Download the latest Knowledge Based Article (KBA) about Roles &amp;
    Permissions at the CEC Digital Workplace.
  </p>
  <p>
    <a
      href="https://usdacts-myit.fed.onbmc.com/dwp/app/#/knowledge/KBA00146910/rkm"
      rel="noopener noreferrer"
      target="_blank"
      class="kba-link"
    >
      View Roles and Permissions KBA
      <mat-icon>arrow_outward</mat-icon>
    </a>
  </p>
  <div class="need-more-help">
    <h4 class="need-more-help-title">Need More Help?</h4>
    <p>
      Visit the
      <a
        href="https://usdacts-myit.fed.onbmc.com/dwp/app/#/"
        rel="noopener noreferrer"
        target="_blank"
        >CEC Digital Workplace</a
      >
      to submit a Customer Help Desk ticket and view other CIAO KBAs.
    </p>
    <p>
      If you have feedback about current or suggested features, email
      <a href="mailto:sm.fs.ciao_feedback@usda.gov">FS-CIAO Feedback</a>.
    </p>
  </div>
</div>
